import { useCallback, useMemo } from 'react';

import i18next from 'i18next';
import { useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { Permissions, SuperUserPermissions } from 'src/app_deprecated/constants/PermissionsConstants';
import UserStore from 'src/app_deprecated/stores/UserStore';

import { ReceiptAuditV2FlagValue } from 'src/app/pages/traceability/metrc-receipt-audit-v2/receipt-audit-v2-flag';
import { useMETRCConfigQuery } from 'src/app/queries/metrc/get-settings';
import { routes } from 'src/app/routes';
import { userDispensariesAtom } from 'src/app/state/user-dispensaries';
import { userPermissionsAtom } from 'src/app/state/user-permissions';
import { isNewYorkLoc, isV4Market } from 'src/app/utils/locale';
import { hasRemoteInitialized } from 'src/app/utils/module-federation';

import { LD } from '../constants/launch-darkly-flags';

import { useBOMPermissions } from './use-bom-permissions';
import { useLDFlag } from './use-ld-flag';
import useSmbPackage from './use-smb-package';

export type BohLinkSubNavItem = {
  isNew?: boolean;
  label: string;
  pastNames?: string[];
  to: string;
  visible: boolean;
};

export type BohLink = {
  isNew?: boolean;
  label: string;
  pastNames?: string[]; // a list of 1. past names of the page (e.g. "product master" is a past name of Catalog), and 2. past names that live on a nested tab within the page (e.g. most Configure sections)
  subNavItems?: BohLinkSubNavItem[];
  to?: string;
  visible: boolean;
};

export function useBohNav(): BohLink[] {
  const { perms, superuserPerms } = useRecoilValue(userPermissionsAtom);
  const user = useRecoilValue(userDispensariesAtom);

  /* SMB filtering */
  // shows a more relevant subset of the nav depending on the business type (Retail, Cultivator, etc).
  const shouldFilterNavForSMB = useLDFlag(LD.SMALL_BUSINESS_PACKS, false);
  const asyncReportsRollout = useLDFlag(LD.ASYNC_REPORTS_ROLLOUT, false);
  const cultivationRefactorRollout = useLDFlag(LD.CULTIVATION_REFACTOR_ROLLOUT, false);
  const moveMetrcToolRollout = useLDFlag(LD.MOVE_METRC_PULL_TOOL_ROLLOUT, false);
  const protectSuperuserUrlsRollout = useLDFlag(LD.PROTECT_SUPERUSER_URLS, false);
  const showBulkUpdateToAdmins = useLDFlag(LD.BULK_UPDATE_ALLOW_ADMIN_UPDATES, false);
  const hideFinancialsTab = useLDFlag(LD.HIDE_FINANCIALS_TAB, false);
  const enableInvoices = useLDFlag(LD.INVOICES_ROLLOUT, false);
  const metrcReceiptAuditV2Rollout = useLDFlag(LD.METRC_RECEIPT_AUDIT_V2_ACCESS, ReceiptAuditV2FlagValue.OFF);
  const smartTagRollout = useLDFlag(LD.SMART_TAGS_ROLLOUT, false) ?? false;
  const brandConnectEnabled = useLDFlag(LD.BRAND_CONNECT_ENABLED, false) ?? false;
  const lagosPocRollout = useLDFlag(LD.LAGOS_POC, false) ?? false;
  const showBackgroundJobStatusPage = useLDFlag(LD.BACKGROUND_JOB_STATUS, false) ?? false;
  const irlEventsRollout = useLDFlag(LD.IRL_EVENTS, false) ?? false;

  const { isRetail, isDelivery, isManufacturer, isCultivator, isDistribution } = useSmbPackage(user.selectedLocation);
  const { canViewBoms, canViewAssemblies } = useBOMPermissions();
  const checkSMBVisibility = useCallback(
    (baseBool: boolean, smbBool: boolean) => {
      if (shouldFilterNavForSMB) {
        // flag is on! Check baseline bool (which may include perms, etc) + SMB bool
        return baseBool && smbBool;
      }
      // flag is off. Just use the baseline bool
      return baseBool;
    },
    [shouldFilterNavForSMB]
  );
  const metrcIntegration = Boolean(
    user.locProfile.UseMETRC && (perms[Permissions.ViewMETRCIntegration] || perms[Permissions.EditMETRCIntegration])
  );
  const bioTrackIntegration = Boolean(
    user.locProfile.UseBioTrack &&
      (perms[Permissions.ViewBioTrackIntegration] || perms[Permissions.EditBioTrackIntegration])
  );
  const usingBioTrackV4 = Boolean(isV4Market({ user }));
  const mmurIntegration = Boolean(
    user.locProfile.UseMMUR &&
      (perms[Permissions.ViewMMURIntegrationConfiguration] || perms[Permissions.EditMMURIntegrationConfiguration])
  );

  const metrcBatchMode = !!perms[Permissions.METRCBatchMode];

  const location = useLocation();
  const isOrgManagementRoute = location.pathname.includes('/organization');
  const isSuperUserVisible =
    !protectSuperuserUrlsRollout || (protectSuperuserUrlsRollout && !!perms[Permissions.SuperUser]);

  const { data: metrcConfig } = useMETRCConfigQuery(user.locProfile.UseMETRC);
  const isMetrcStateNY = metrcConfig?.StateCode?.toLowerCase() === 'ny';

  const links: BohLink[] = useMemo(() => {
    const links: BohLink[] = [
      {
        label: 'Home',
        to: '/home',
        visible: checkSMBVisibility(true, isRetail && !isOrgManagementRoute),
      },
      {
        label: 'Products',
        subNavItems: [
          {
            label: 'Inventory',
            to: routes.products.inventory.index,
            visible: !!perms[Permissions.ViewInventory],
            pastNames: ['receive inventory', 'receive history'],
          },
          {
            label: 'Catalog',
            to: routes.products.catalog.index,
            visible: !!perms[Permissions.ViewProductMaster],
            pastNames: ['product master'],
          },
          {
            label: 'Manifests',
            to: routes.products.manifests.index,
            visible: !!(perms[Permissions.ViewManifest] || perms[Permissions.EditManifest]),
          },
          {
            label: 'Purchase orders',
            to: routes.products.purchaseOrders.index,
            visible: user.locProfile.HasPurchasOrders && !!perms[Permissions.ReceiveInventory],
            pastNames: ['pending purchase orders'],
          },
          {
            label: 'Retailer purchase orders',
            to: routes.products.retailerPurchaseOrders,
            // Once we implement a persona switcher, we won't be checking CompanyFlag. So as not to add production risk,
            // we'll keep that part commented out to just be used locally.
            visible: lagosPocRollout, //  && locInfo?.LocInfo.CompanyFlag === 'Dispensary'
          },
          {
            label: 'Brand purchase orders',
            to: routes.products.brandPurchaseOrders,
            // Once we implement a persona switcher, we won't be checking CompanyFlag. So as not to add production risk,
            // we'll keep that part commented out to just be used locally.
            visible: lagosPocRollout, // && locInfo?.LocInfo.CompanyFlag === 'Distribution'
          },
          {
            label: 'Invoices',
            to: routes.products.invoices.index,
            visible:
              ((enableInvoices && isNewYorkLoc({ user })) ?? false) &&
              Boolean(perms[Permissions.ViewBioTrackInvoices] || perms[Permissions.EditBioTrackInvoices]),
          },
          {
            label: 'Orders',
            to: routes.products.orders,
            visible:
              ((user.locProfile.UseOrderFulfill || user.locProfile.NoDoublePreOrder) &&
                !!perms[Permissions.ViewManifest]) ||
              (user.locProfile.UseOrderFulfill && user.locProfile.OrdersbyProduct && !!perms[Permissions.ViewManifest]),
          },
          {
            label: 'Audits',
            to: routes.products.audits.index,
            visible: !!perms[Permissions.ViewReconciliation],
            pastNames: ['inventory reconciliation'],
          },
          {
            label: 'Journal',
            to: routes.products.journal,
            visible: !!perms[Permissions.ViewInventoryHistory],
          },
          {
            label: 'Vendors',
            to: routes.products.vendors,
            visible: !!(perms[Permissions.EditVendors] || perms[Permissions.ViewVendors]),
          },
          {
            label: 'Manufacturers',
            to: routes.products.manufacturers,
            visible:
              user.locProfile.UseManufacturers &&
              !!(perms[Permissions.EditProducers] || perms[Permissions.ViewProducers]),
          },
          {
            label: 'Brands',
            to: routes.products.brands,
            visible: !!(perms[Permissions.EditBrands] || perms[Permissions.ViewBrands]),
          },
          {
            label: 'Strains',
            to: routes.products.strains,
            visible: !!(perms[Permissions.EditStrains] || perms[Permissions.ViewStrains]),
          },
          {
            label: 'Smart tags',
            to: routes.products.smartTags,
            visible:
              smartTagRollout &&
              (!!perms[Permissions.EditTags] || !!perms[Permissions.CreateTags] || !!perms[Permissions.DeleteTags]),
          },
          {
            label: 'Configure',
            to: routes.products.configure.index,
            visible:
              !!perms[Permissions.EditProductCategories] ||
              !!perms[Permissions.ViewProductCategories] ||
              !!perms[Permissions.EditPricing] ||
              !!perms[Permissions.ViewPricing] ||
              !!perms[Permissions.EditSizes] ||
              !!perms[Permissions.ViewSizes] ||
              !!perms[Permissions.EditTags] ||
              !!perms[Permissions.CreateTags] ||
              !!perms[Permissions.DeleteTags] ||
              (!!perms[Permissions.Administrator] && user.locProfile.FormValidation) ||
              !!perms[Permissions.EditInventoryStatus] ||
              !!perms[Permissions.ViewInventoryStatus] ||
              (!user.locProfile.UseBioTrack &&
                !user.locProfile.UseMETRC &&
                (!!perms[Permissions.EditInventoryStatus] || !!perms[Permissions.ViewInventoryStatus])) ||
              (user.locProfile.EcomV2 && !!perms[Permissions.ViewProductMaster]) ||
              !!perms[Permissions.ViewDosages] ||
              !!perms[Permissions.EditDosages] ||
              !!perms[Permissions.EditPackageIdFormat] ||
              !!perms[Permissions.ViewPackageIdentityFormat] ||
              !!perms[Permissions.EditProductSkuFormat] ||
              !!perms[Permissions.ViewProductSkuFormat] ||
              !!perms[Permissions.EditBatchIdFormat] ||
              !!perms[Permissions.ViewBatchIdFormat] ||
              !!perms[Permissions.EditDeliveryTitleFormat] ||
              !!perms[Permissions.ViewDeliveryTitleFormat],
            pastNames: [
              'adjustment reasons',
              'dosages',
              'fields',
              'form validation',
              'identifiers',
              'inventory status',
              'pricing',
              'product categories',
              'product groups',
              'sizes',
              'tags',
            ],
          },
          {
            label: 'Help center',
            to: 'https://support.dutchie.com/hc/en-us/sections/12882290236179-Products',
            visible: true,
          },
        ],
        visible: checkSMBVisibility(true, isRetail && !isOrgManagementRoute),
      },
      {
        label: 'Financials',
        to: routes.financials.index,
        visible:
          checkSMBVisibility(true, isRetail && !isOrgManagementRoute) &&
          !!perms[Permissions.ViewMLSR] &&
          !hideFinancialsTab,
      },
      {
        label: 'Reports',
        subNavItems: [
          {
            label: 'Overview',
            to: routes.reportsV2.overview.index,
            visible: !!perms[Permissions.ViewDashboard],
          },
          {
            label: 'Closing report',
            to: routes.reportsV2.closingReport.index,
            visible: !!perms[Permissions.ViewClosingReport],
          },
          {
            label: 'Sales',
            to: routes.reportsV2.sales.index,
            visible: !!perms[Permissions.ViewSalesReports],
          },
          {
            label: 'Inventory',
            to: routes.reportsV2.inventory.index,
            visible: !!perms[Permissions.ViewInventoryReports],
          },
          {
            label: 'Employee',
            to: routes.reportsV2.employee.index,
            visible: !!perms[Permissions.ViewEmployeeReports],
          },
          {
            label: 'Marketing',
            to: routes.reportsV2.marketing,
            visible: !!perms[Permissions.ViewMarketingReports],
          },
          {
            label: 'Operations',
            to: routes.reportsV2.operations,
            visible: !!perms[Permissions.ViewOperationsReports],
          },
          {
            label: 'BI tools',
            to: routes.reportsV2.biTools.index,
            visible:
              user.locProfile.HasLooker && (!!perms[Permissions.ViewBIDashboard] || !!perms[Permissions.ExploreBIData]),
          },
          {
            label: 'Queued reports',
            to: routes.reportsV2.asyncReports,
            visible: !!asyncReportsRollout,
          },
          {
            label: 'Configure',
            to: routes.reportsV2.configure,
            visible: !!perms[Permissions.ViewStaticReports] && !!perms[Permissions.ScheduleEmailReports],
          },
          {
            label: 'Help center',
            to: 'https://support.dutchie.com/hc/en-us/sections/12882401045523-Reports',
            visible: true,
          },
        ],
        visible: checkSMBVisibility(true, isRetail && !isOrgManagementRoute),
      },
      {
        label: 'Customers',
        subNavItems: [
          {
            label: 'Customers',
            to: routes.customers.customers,
            visible: !!perms[Permissions.ViewCustomers],
          },
          {
            label: 'Customer groups',
            to: routes.customers.customerGroups,
            visible: !!perms[Permissions.EditDiscountGroups] || !!perms[Permissions.ViewDiscountGroups],
            pastNames: ['discount groups'],
          },
          {
            label: 'Segments',
            to: routes.customers.segments.index,
            visible: !!perms[Permissions.ViewSegments] || !!perms[Permissions.EditSegments],
          },
          {
            label: 'Configure',
            to: routes.customers.configure,
            visible:
              !!perms[Permissions.EditDoctors] ||
              !!perms[Permissions.ViewDoctors] ||
              (!!perms[Permissions.Administrator] &&
                (!UserStore.isFieldHidden('Customer profile (Register)', 'PrimaryQualifyingConditionId') ||
                  !UserStore.isFieldHidden('Customer profile (back office)', 'PrimaryQualifyingConditionId'))) ||
              (!!perms[Permissions.Administrator] && user.locProfile.FormValidation),
            pastNames: ['doctors', 'qualifying conditions', 'form validation', 'fields'],
          },
          {
            label: 'Help center',
            to: 'https://support.dutchie.com/hc/en-us/sections/12882503114515-Customers',
            visible: true,
          },
        ],
        visible: checkSMBVisibility(true, (isRetail || isDistribution) && !isOrgManagementRoute),
      },
      {
        label: 'Registers',
        subNavItems: [
          {
            label: 'Overview',
            to: routes.registers.overview,
            visible: !!perms[Permissions.ViewPOSSummary],
            pastNames: [
              'bulk register transactions',
              'close out',
              'drop cash',
              'launch pos',
              'pos daily activities summary',
              'pos terminal',
            ],
          },
          {
            label: 'Transactions',
            to: routes.registers.transactions.index,
            visible: !!perms[Permissions.ViewRegisterTransactionData],
            pastNames: [
              'pos daily activities',
              'bulk register transactions',
              'drop cash',
              'close out',
              'register transactions',
            ],
          },
          {
            label: 'Configure',
            to: routes.registers.configure.index,
            visible:
              !!user.locProfile.POSStatus ||
              !!perms[Permissions.EditTransactionAdjustmentReasons] ||
              !!perms[Permissions.EditReturnReasons] ||
              !!perms[Permissions.ViewReturnReasons] ||
              user.locProfile.CustomerVisitReason ||
              !!perms[Permissions.Administrator],
            pastNames: [
              'cancellation reasons',
              'cards',
              'guestlist status',
              'order workflow',
              'pos layout',
              'register adjustments',
              'return reasons',
              'transaction adjustment reasons',
              'void reasons',
            ],
          },
          {
            label: 'Help center',
            to: 'https://support.dutchie.com/hc/en-us/sections/12882501190675-Registers',
            visible: true,
          },
        ],
        visible: checkSMBVisibility(
          true,
          isRetail && !isDistribution && !isCultivator && !isManufacturer && !isOrgManagementRoute
        ),
      },
      {
        label: 'Marketing',
        subNavItems: [
          {
            label: 'Discounts',
            to: routes.marketing.discounts.index,
            visible: !!(perms[Permissions.EditDiscounts] || perms[Permissions.ViewDiscounts]),
          },
          {
            label: 'Loyalty',
            to: routes.marketing.loyalty.settings,
            visible:
              user.locProfile.ShowLeafLogixLoyalty &&
              (!!perms[Permissions.EditLoyaltyAccrual] || !!perms[Permissions.ViewLoyaltyAccrual]),
            pastNames: ['adjust loyalty reasons'],
          },
          {
            label: 'External loyalty',
            to: routes.marketing.externalLoyalty.index,
            visible: user.locProfile.ShowExternalLoyalty && !!perms[Permissions.ConfigureExternalLoyalty],
          },
          {
            label: 'Campaigns',
            to: routes.marketing.campaigns.index,
            visible:
              !!user.locProfile.LoyaltyAndMarketingSubscriber &&
              (!!perms[Permissions.AccessEmailMarketing] ||
                !!perms[Permissions.ViewCampaigns] ||
                !!perms[Permissions.CreateEditCampaigns] ||
                !!perms[Permissions.SendCampaigns]) &&
              (!!user.locProfile.UseEmailMarketing ||
                !!user.locProfile.UseSMSMarketing ||
                !!user.locProfile.UsePushMarketing),
          },
          {
            label: 'Workflows',
            to: routes.marketing.workflows.index,
            visible:
              !!user.locProfile.LoyaltyAndMarketingSubscriber &&
              !!user.locProfile.UseWorkflowMarketing &&
              (!!perms[Permissions.AccessEmailMarketing] ||
                !!perms[Permissions.ViewCampaigns] ||
                !!perms[Permissions.CreateEditCampaigns] ||
                !!perms[Permissions.SendCampaigns]),
          },
          {
            label: 'Events',
            to: routes.marketing.events.index,
            visible: irlEventsRollout && !!user.locProfile.LoyaltyAndMarketingSubscriber,
          },
          {
            label: 'Configure',
            to: routes.marketing.configure.index,
            visible:
              !!user.locProfile.LoyaltyAndMarketingSubscriber &&
              (!!perms[Permissions.ViewEmailTemplates] ||
                !!perms[Permissions.CreateEditEmailTemplates] ||
                !!perms[Permissions.AccessEmailMarketing] ||
                !!perms[Permissions.ViewCampaigns] ||
                !!perms[Permissions.CreateEditCampaigns] ||
                !!perms[Permissions.SendCampaigns]),
          },
          {
            label: 'Help center',
            to: 'https://support.dutchie.com/hc/en-us/sections/12882550913043-Marketing',
            visible: true,
          },
        ],
        visible: checkSMBVisibility(
          true,
          isRetail && !isDistribution && !isCultivator && !isManufacturer && !isOrgManagementRoute
        ),
      },
      {
        label: 'Delivery',
        subNavItems: [
          {
            label: 'Configure',
            to: routes.delivery.configure.index,
            visible:
              ((user.locProfile.EnableDelivery || user.locProfile.WholesaleCarsAndDrivers) &&
                !!perms[Permissions.EditCars]) ||
              !!perms[Permissions.ViewCars] ||
              !!perms[Permissions.EditDrivers] ||
              !!perms[Permissions.ViewDrivers] ||
              !!perms[Permissions.EditZones] ||
              !!perms[Permissions.ViewZones] ||
              !!perms[Permissions.EditThresholds] ||
              !!perms[Permissions.ViewThresholds] ||
              !!perms[Permissions.EditDeliveryTitleFormat] ||
              !!perms[Permissions.ViewDeliveryTitleFormat],
            pastNames: ['delivery management', 'cars', 'drivers', 'zones', 'thresholds', 'delivery title format'],
          },
          {
            label: 'Help center',
            to: 'https://support.dutchie.com/hc/en-us/sections/12882649521043-Delivery',
            visible: true,
          },
        ],
        visible: checkSMBVisibility(
          user.locProfile.EnableDelivery || user.locProfile.WholesaleCarsAndDrivers,
          isRetail && !isOrgManagementRoute
        ),
      },
      {
        label: 'Brand Connect',
        to: routes['brand-connect'].brands.index,
        visible: hasRemoteInitialized('backoffice-remote-brands') && brandConnectEnabled,
        subNavItems: [
          {
            label: 'Brands',
            to: routes['brand-connect'].brands.index,
            visible: true,
          },
          {
            label: 'Brand Catalog',
            to: routes['brand-connect'].catalog.index,
            visible: true,
          },
          {
            label: 'Connections',
            to: routes['brand-connect'].connections.index,
            visible: true, // TODO: add permission check once Connections permissions exist
          },
          {
            label: 'Help center',
            to: 'https://support.dutchie.com/hc/en-us/sections/12882290236179-Products', // todo: add correct help center link when it becomes available
            visible: true,
          },
        ],
      },
      {
        label: 'Cultivation',
        subNavItems: [
          {
            label: 'API queue',
            to: routes.cultivation.cultivationApiQueue,
            visible:
              user.locProfile.APIActionsQueued &&
              (!!perms[Permissions.ViewCultivationApiQueue] || !!perms[Permissions.EditCultivationApiQueue]),
          },
          {
            label: 'Plants',
            to: routes.cultivation.plants.index,
            visible: !!perms[Permissions.ViewPlants],
            pastNames: [
              'plant manager',
              'plant group manager',
              'scan plants into location',
              'scan plants into harvest',
              'scan plants into plant rooms',
              'scan plants into retirement',
              'move plant subrooms into plant rooms',
              'add plant/clones to inventory',
            ],
          },
          {
            label: 'Plant groups',
            to: routes.cultivation.plantGroups,
            visible: !!perms[Permissions.ViewPlantGroups] && (cultivationRefactorRollout ?? false), // Only show the separate Plant groups nav item when the refactor flag is enabled
          },
          {
            label: 'Batches',
            to: routes.cultivation.batches,
            visible: !!perms[Permissions.ViewPlantGroups],
            pastNames: ['batch manager'],
          },
          {
            label: 'Harvests',
            to: routes.cultivation.harvests,
            visible: !!perms[Permissions.ViewPlantGroups],
            pastNames: ['harvest efficiency'],
          },
          {
            label: 'Waste',
            to: routes.cultivation.waste,
            visible: !!perms[Permissions.ViewWasteManager],
            pastNames: ['waste manager'],
          },
          {
            label: 'Audits',
            to: routes.cultivation.audits,
            visible: user.locProfile.PlantReconciliation,
            pastNames: ['cultivation audits', 'plant reconciliation'],
          },
          {
            label: 'Configure',
            to: routes.cultivation.configure.index,
            visible: !!perms[Permissions.EditReasonCodes] || !!perms[Permissions.ViewReasonCodes],
            pastNames: ['cultivation adjustments', 'stages'],
          },
          {
            label: 'Help center',
            to: 'https://support.dutchie.com/hc/en-us/sections/12882746201619-Cultivation',
            visible: true,
          },
        ],
        // TODO: check with Heather on this one. Should Cultivation be conditional based on isCultivator?
        visible: checkSMBVisibility(!!user.locProfile.IsLicensed, isCultivator && !isOrgManagementRoute),
      },
      {
        label: 'Manufacturing',
        subNavItems: [
          {
            label: 'Recipes',
            to: routes.manufacturing.recipes,
            visible:
              !!perms[Permissions.ViewRecipes] ||
              !!perms[Permissions.ViewRecipeBatch] ||
              !!perms[Permissions.EditRecipe],
            pastNames: ['recipe batches'],
          },
          {
            label: 'Bill of materials',
            to: routes.manufacturing.billOfMaterials,
            visible: canViewBoms,
          },
          {
            label: 'Assemblies',
            to: routes.manufacturing.assemblies,
            visible: canViewAssemblies,
          },
          {
            label: 'Infusions',
            to: routes.manufacturing.infusions,
            visible: user.locProfile.UseInfusions && !!perms[Permissions.ViewInfusions],
            pastNames: ['infusion batches'],
          },
          {
            label: 'Distillations',
            to: routes.manufacturing.distillations,
            visible:
              (perms[Permissions.EditDistillations] || perms[Permissions.ViewDistillations]) &&
              user.locProfile.UseDistillationMethod,
            pastNames: ['distillations'],
          },
        ],
        visible: checkSMBVisibility(
          user.locProfile.NewIAManufacturingModule &&
            (!!perms[Permissions.ViewRecipes] ||
              !!perms[Permissions.ViewRecipeBatch] ||
              !!perms[Permissions.EditRecipe]),
          ((isDistribution && isDelivery) || isManufacturer) && !isOrgManagementRoute
        ),
      },
      {
        label: 'Traceability',
        subNavItems: [
          {
            label: 'Integration audit',
            to: routes.traceability.integrationAudit,
            visible:
              !!perms[Permissions.ViewIntegrationReconciliation] &&
              (user.locProfile.UseMETRC || user.locProfile.UseBioTrack),
            pastNames: ['integration reconciliation'],
          },
          {
            label: 'Receipt audit',
            to: routes.traceability.receiptAudit,
            visible: (() => {
              const hasViewPermission = !!perms[Permissions.ViewReceiptAudit];
              const hasIntegration = user.locProfile.UseMETRC || usingBioTrackV4;

              // Hide v1 if: non-superuser AND NY state (they should use v2, regardless of flag state)
              if (!isSuperUserVisible && isMetrcStateNY) {
                return false;
              }

              // Otherwise show v1 with existing logic
              return hasViewPermission && hasIntegration;
            })(),
            pastNames: ['receipt reconciliation'],
          },
          {
            label: 'Receipt audit',
            to: routes.traceability.receiptAuditV2,
            visible: (() => {
              const flagValue = metrcReceiptAuditV2Rollout ?? ReceiptAuditV2FlagValue.OFF;
              const flagEnabled = flagValue > ReceiptAuditV2FlagValue.OFF;
              const hasViewPermission = !!perms[Permissions.ViewReceiptAudit];

              // Base requirements: permission, Metrc enabled, and flag enabled
              if (!hasViewPermission || !user.locProfile.UseMETRC || !flagEnabled) {
                return false;
              }

              // Show v2 if NY state OR superuser
              return isMetrcStateNY || isSuperUserVisible;
            })(),
          },
          {
            label: 'Cultivation integration audit',
            to: routes.traceability.cultivationIntegrationAudit,
            visible:
              user.locProfile.IsLicensed &&
              !!perms[Permissions.ViewIntegrationReconciliation] &&
              (user.locProfile.UseMETRC || user.locProfile.UseBioTrack) &&
              user.locProfile.ShowCultivationIntegrationReconcilation,
            pastNames: ['integration reconciliation'],
          },
          {
            label: 'Logs',
            to: routes.traceability.logs.index,
            visible: metrcIntegration || metrcBatchMode || mmurIntegration || bioTrackIntegration,
            pastNames: ['metrc', 'batch mode logs'],
          },
          {
            label: 'Help center',
            to: 'https://support.dutchie.com/hc/en-us/sections/12882744815763-Traceability',
            visible: true,
          },
        ],
        visible: checkSMBVisibility(true, (isRetail || isDistribution) && !isOrgManagementRoute),
      },
      {
        label: 'Settings',
        subNavItems: [
          {
            label: 'Account',
            to: routes.settings.account,
            visible: true,
          },
          {
            label: 'Location',
            to: routes.settings.location,
            visible: !!perms[Permissions.ViewPasswordSettings] || !!perms[Permissions.Administrator],
            pastNames: ['location options', 'password settings', 'security'],
          },
          {
            label: 'Users',
            to: routes.settings.users.index,
            visible:
              !!perms[Permissions.EditUsers] ||
              !!perms[Permissions.ViewUsers] ||
              !!perms[Permissions.EditUserStateIds] ||
              !!perms[Permissions.ViewPermissions],
            pastNames: ['user permission groups'],
          },
          {
            label: 'Devices',
            to: routes.settings.devices,
            visible: !!perms[Permissions.EditRegisters] || !!perms[Permissions.ViewRegisters],
            pastNames: ['pos maintenance registers', 'printers'],
          },
          {
            label: 'Receipts & Tickets',
            to: routes.settings.receiptsAndTickets,
            visible: !!perms[Permissions.ConfigureReceipts],
          },
          {
            label: 'Rooms',
            to: routes.settings.rooms.index,
            visible:
              !!perms[Permissions.ViewRooms] ||
              !!perms[Permissions.EditRooms] ||
              !!perms[Permissions.ViewTables] ||
              !!perms[Permissions.EditTables],
            pastNames: ['subrooms', 'tables'],
          },
          {
            label: 'Labels',
            to: routes.settings.labels,
            visible: !!perms[Permissions.ConfigureLabels],
            pastNames: ['label library', 'image library'],
          },
          {
            label: 'Taxes & compliance',
            to: routes.settings.taxesAndCompliance.index,
            visible: true, // no permission key found in `src/app_deprecated/constants/PermissionsConstants`
            pastNames: ['customer views audit records', 'purchase limits'],
          },
          {
            label: 'Fees & donations',
            to: routes.settings.feesDonations,
            visible:
              user.locProfile.UseFeesDonations &&
              (!!perms[Permissions.EditFeesDonations] || !!perms[Permissions.ViewFeesAndDonations]),
            pastNames: ['fees and donations'],
          },
          {
            label: 'Integrations',
            to: routes.settings.integrations.index,
            visible: true,
            pastNames: [
              'metrc',
              'biotrack',
              'canpay',
              'adilas',
              'mmur',
              'pmp upload',
              'sso',
              'masscip',
              'weed maps 2',
              'linx',
              'cashless atm',
              'leafly',
              'leaflink',
              'spin payments',
              'leafpay',
              'biomauris',
              'iowa-dph',
              'spring big',
              'alpine iq',
              'fyllo',
              'hypur',
              'quickbooks',
              'mn omc',
              'onfleet',
              'tookan',
              'workwave',
              'leaf data',
              'ccrs',
              'idscan',
              'sprout configuration',
            ],
          },
          {
            label: 'Job status',
            to: routes.settings.jobStatus.index,
            visible: showBackgroundJobStatusPage,
          },
          {
            label: 'Time clock',
            to: routes.settings.timeClock,
            visible: true,
          },
          {
            label: 'Bulk Update',
            to: routes.settings.bulkUpdateData,
            visible: !!showBulkUpdateToAdmins && !!perms[Permissions.Administrator],
          },
          {
            label: 'Help center',
            to: 'https://support.dutchie.com/hc/en-us/sections/12882796400531-Settings',
            visible: true,
          },
        ],
        visible: checkSMBVisibility(true, isRetail && !isOrgManagementRoute),
      },
      {
        label: 'Superuser',
        subNavItems: [
          {
            label: 'Superuser tools',
            to: routes.superuser.tools,
            visible: isSuperUserVisible,
          },
          {
            label: 'Superuser logs',
            to: routes.superuser.logs,
            visible: isSuperUserVisible,
          },
          {
            label: 'Superuser management',
            to: routes.superuser.management,
            visible: !!superuserPerms[SuperUserPermissions.ManageSuperUsers],
          },
          {
            label: 'SSO global config',
            to: routes.superuser.ssoGlobalconfig,
            visible: isSuperUserVisible,
          },
          {
            label: 'Manifest options',
            to: routes.superuser.manifestOptions,
            visible: isSuperUserVisible,
          },
          {
            label: 'Audit tables',
            to: routes.superuser.auditTables,
            visible: isSuperUserVisible,
          },
          {
            label: 'Upload provincial catalog',
            to: routes.superuser.provincialCatalogUpload,
            visible: isSuperUserVisible,
          },
          {
            label: 'METRC tools',
            to: routes.superuser.metrcTools,
            visible: !moveMetrcToolRollout && isSuperUserVisible,
          },
          {
            label: 'Feature flags',
            to: routes.superuser.features,
            visible: isSuperUserVisible,
          },
          {
            label: 'Edit company',
            to: routes.superuser.editLsp,
            visible: isSuperUserVisible,
          },
          {
            label: 'Create company',
            to: routes.superuser.createLsp,
            visible: !!superuserPerms[SuperUserPermissions.AccountCreation] && isSuperUserVisible,
          },
          {
            label: 'Public API',
            to: routes.superuser.publicApi,
            visible: isSuperUserVisible,
          },
          {
            label: 'Looker',
            to: routes.superuser.lookerAdmin,
            visible: isSuperUserVisible,
          },
          {
            label: 'Documentation',
            to: routes.superuser.documentation,
            visible: isSuperUserVisible,
          },
          {
            label: 'Bulk update',
            to: routes.superuser.bulkUpdateData,
            visible: isSuperUserVisible,
          },
          {
            label: 'State packs',
            to: routes.superuser.licenseTypes,
            visible: !!superuserPerms[SuperUserPermissions.StatePackModification] && isSuperUserVisible,
          },
          {
            label: 'Data loader',
            to: '/superuser/data-import',
            visible:
              !user.selectedLocation.IsLive &&
              !!superuserPerms[SuperUserPermissions.AccessDataImport] &&
              isSuperUserVisible,
          },
          {
            label: 'Data wipe',
            to: routes.superuser.dataWipe,
            visible:
              !user.selectedLocation.IsLive &&
              !!superuserPerms[SuperUserPermissions.AccessDataWipe] &&
              isSuperUserVisible,
          },
          {
            label: 'Publish location',
            to: routes.superuser.publishLocation,
            visible:
              ((!user.selectedLocation.IsLive && !!superuserPerms[SuperUserPermissions.PublishLocation]) ||
                (user.selectedLocation.IsLive && !!superuserPerms[SuperUserPermissions.RevertToStaging])) &&
              isSuperUserVisible,
          },
          {
            label: 'Location manager',
            to: routes.superuser.locationManager,
            visible:
              isSuperUserVisible &&
              (Boolean(superuserPerms[SuperUserPermissions.AccessLocationManager]) ||
                Boolean(superuserPerms[SuperUserPermissions.SaveBulkChangesInLocationManager])),
          },
        ],
        visible: checkSMBVisibility(!!perms[Permissions.SuperUser], true && !isOrgManagementRoute),
      },
    ];

    const localizeLabelsDFS = (links: BohLink[]) => {
      links.forEach((link) => {
        link.label = i18next.t(link.label);
        if (link.subNavItems) {
          localizeLabelsDFS(link.subNavItems);
        }
      });
    };

    localizeLabelsDFS(links);

    return links;
  }, [
    checkSMBVisibility,
    isRetail,
    perms,
    user.locProfile,
    user.selectedLocation.IsLive,
    asyncReportsRollout,
    isDistribution,
    isCultivator,
    isManufacturer,
    cultivationRefactorRollout,
    isDelivery,
    usingBioTrackV4,
    metrcIntegration,
    metrcBatchMode,
    mmurIntegration,
    bioTrackIntegration,
    superuserPerms,
    moveMetrcToolRollout,
    isOrgManagementRoute,
    hideFinancialsTab,
    metrcReceiptAuditV2Rollout,
    isMetrcStateNY,
    isSuperUserVisible,
    irlEventsRollout,
  ]);

  return links;
}
